import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, Grid, Tab, TextField, withStyles } from '@material-ui/core';
import Card from 'src/components/Card';
import { FaUserTie as CustomerIcon } from '@react-icons/all-files/fa/FaUserTie';
import { FaRegUser as EmployeeIcon } from '@react-icons/all-files/fa/FaRegUser';
import { FaRegAddressCard as ContactIcon } from '@react-icons/all-files/fa/FaRegAddressCard';
import ResultsSection from './components/ResultsSection';
import SwipeableViews from 'react-swipeable-views';
import { Tabs } from 'src/components/Tabs';
import { TabBadge } from './components/TabBadge';
import { debounce } from 'lodash';
import { MdSearch as SearchIcon } from '@react-icons/all-files/md/MdSearch';
import useGlobalSearch, { PaginatedResults } from '../../hooks/useGlobalSearch';
import { Fetchable } from 'src/interfaces/api/api';

enum TabTypes {
  Contacts = 'contacts',
  Customers = 'customers',
  Employees = 'employees',
}

const routes = [
  {
    key: TabTypes.Customers,
    label: 'Clients',
    hash: '#clients',
    icon: <CustomerIcon />,
    component: ResultsSection,
  },
  {
    key: TabTypes.Contacts,
    label: 'Contacts',
    hash: '#contacts',
    icon: <ContactIcon />,
    component: ResultsSection,
  },
  {
    key: TabTypes.Employees,
    label: 'Employés',
    hash: '#employes',
    icon: <EmployeeIcon />,
    component: ResultsSection,
  },
];

const SearchTab = withStyles({
  wrapper: {
    flexDirection: 'row',
    '& > svg': {
      marginRight: 5,
    },
  },
  labelIcon: {
    minHeight: 0,
  },
})(Tab);

interface Props {
  query: string;
  pageHash?: string;
  onChangePageHash: (newHash: string) => void;
  onChangeQuery: (newQuery: string) => void;
}

const SearchResultsCard: FC<Props> = ({ query, onChangeQuery, pageHash, onChangePageHash }) => {
  const {
    search,
    contacts,
    customers,
    employees,
    setEmployeePage,
    setCustomerPage,
    setContactPage,
  } = useGlobalSearch();
  const startPageIndex = routes.findIndex((route) => route.hash === pageHash);

  const [currentTab, setCurrentTab] = useState(startPageIndex === -1 ? 0 : startPageIndex);

  useEffect(() => {
    search(query);
  }, [query]);

  const handleTabChange = (event, newValue) => {
    handlePageChange(newValue);
  };

  const handlePageChange = (newValue) => {
    onChangePageHash(routes[newValue].hash);
    setCurrentTab(newValue);
  };

  const renderTab = useCallback(
    (route: typeof routes[0], hidden: boolean) => {
      const Component = route.component;

      switch (route.key) {
        case TabTypes.Contacts:
          return <Component key={route.key} results={contacts} hidden={hidden} onPageChange={setContactPage} />;
        case TabTypes.Employees:
          return <Component key={route.key} results={employees} hidden={hidden} onPageChange={setEmployeePage} />;
        case TabTypes.Customers:
          return <Component key={route.key} results={customers} hidden={hidden} onPageChange={setCustomerPage} />;
        default:
          return null;
      }
    },
    [customers, employees, contacts]
  );

  const handleQueryChange = debounce((value: string) => {
    onChangeQuery(value);
  }, 600);

  const getFetchableCount = useCallback((fetchable: Fetchable<PaginatedResults>): string => {
    if (fetchable.status !== 'fetched') {
      return '0';
    }
    return `${fetchable.data.items.length || 0}`;
  }, []);

  const getCategoryCount = useCallback(
    (category: TabTypes): string => {
      switch (category) {
        case TabTypes.Contacts:
          return getFetchableCount(contacts);
        case TabTypes.Employees:
          return getFetchableCount(employees);
        case TabTypes.Customers:
          return getFetchableCount(customers);
        default:
          return '0';
      }
    },
    [contacts, employees, customers, getFetchableCount]
  );

  return (
    <Card>
      <Grid justifyContent='center' container spacing={2}>
        <Box display='flex' alignItems='center' clone>
          <Grid item xs={12}>
            <TextField
              size='small'
              variant='outlined'
              placeholder='Recherche employé, contact, client, ...'
              InputProps={{
                endAdornment: <SearchIcon color='#828282' size={20} />,
              }}
              defaultValue={query}
              onChange={(e) => handleQueryChange(e.target.value || '')}
              fullWidth
            />
          </Grid>
        </Box>

        <Grid item xs={12}>
          <Tabs
            indicatorColor='primary'
            textColor='primary'
            variant='scrollable'
            scrollButtons='auto'
            value={currentTab}
            onChange={handleTabChange}
          >
            {routes.map((route, key) => (
              <SearchTab
                key={key}
                label={
                  <Box display='flex'>
                    {route.label} <TabBadge label={getCategoryCount(route.key)} marginLeft='10px' />
                  </Box>
                }
                icon={route.icon}
                value={key}
              />
            ))}
          </Tabs>
        </Grid>

        <Grid item xs={12}>
          <SwipeableViews index={currentTab} onChangeIndex={handlePageChange}>
            {routes.map((route, key) => renderTab(route, key !== currentTab))}
          </SwipeableViews>
        </Grid>
      </Grid>
    </Card>
  );
};

export default SearchResultsCard;
